'use client';

import { c as _c } from "react/compiler-runtime";
import { formatUSD } from '@shared/utils/helpers/formatCurrency';
import { unwrapFromResult } from '@shared/utils/helpers/resultWrapper';
import toast from '@shared/utils/helpers/toast';
import useUser from '@shared/utils/hooks/useUser';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { animDelay } from '../../utils/animDelay';
import { client } from '../../utils/trpc';
import { useAuthModals } from '../../widgets/WidgetsContext';
import Confirmation from '../common/Confirmation';
import Error from '../common/Error';
const PurchaseHistory = () => {
  const $ = _c(33);
  const [cancelSubscriptionModalOpen, setCancelSubscriptionModalOpen] = useState(null);
  const [cancelSubButtonLoading, setCancelSubButtonLoading] = useState(false);
  const [isRecovering, setIsRecovering] = useState(false);
  const {
    currentUser
  } = useUser();
  const {
    openAuthContinueModal
  } = useAuthModals();
  const queryClient = useQueryClient();
  const recoveryAttempted = useRef(false);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = ["listPurchases"];
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const t1 = currentUser.isAuthenticated === true;
  let t2;
  if ($[1] !== t1) {
    t2 = {
      queryKey: t0,
      queryFn: _temp,
      enabled: t1
    };
    $[1] = t1;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const {
    isLoading,
    refetch,
    data,
    error
  } = useQuery(t2);
  let t3;
  if ($[3] !== currentUser.isAuthenticated || $[4] !== queryClient || $[5] !== refetch) {
    t3 = () => {
      if (!currentUser.isAuthenticated || recoveryAttempted.current) {
        return;
      }
      recoveryAttempted.current = true;
      let ignore = false;
      (async () => {
        setIsRecovering(true);
        const [err, result] = await client.products.recoverPendingStripePayments.mutate();
        if (ignore) {
          return;
        }
        if (err) {
          setIsRecovering(false);
          return;
        }
        if (result.recovered > 0) {
          toast("Purchases recovered", `${result.recovered} pending payment(s) credited.`, "success");
          refetch();
          queryClient.invalidateQueries({
            queryKey: ["currentUser"]
          });
        }
        setIsRecovering(false);
      })();
      return () => {
        ignore = true;
      };
    };
    $[3] = currentUser.isAuthenticated;
    $[4] = queryClient;
    $[5] = refetch;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== currentUser.isAuthenticated) {
    t4 = [currentUser.isAuthenticated];
    $[7] = currentUser.isAuthenticated;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  useEffect(t3, t4);
  let t5;
  if ($[9] !== refetch) {
    t5 = async (id, confirm) => {
      if (confirm) {
        setCancelSubButtonLoading(true);
        const [error_0] = await client.products.cancelSubscription.mutate({
          subscriptionId: id
        });
        if (error_0) {
          return;
        }
        await refetch();
        setCancelSubscriptionModalOpen(null);
      } else {
        setCancelSubscriptionModalOpen(id);
      }
    };
    $[9] = refetch;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const cancelSubscriptionHandler = t5;
  let t6;
  if ($[11] !== isRecovering) {
    t6 = isRecovering && <div className="mx-auto max-w-5xl px-4 pt-4"><div className="flex items-center gap-3 rounded-lg border border-primary/30 bg-primary/10 p-3 text-sm text-primary"><div className="size-4 animate-spin rounded-full border-2 border-primary border-t-transparent" /><span>Completing your previous purchase...</span></div></div>;
    $[11] = isRecovering;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== cancelSubscriptionHandler || $[14] !== currentUser.isAuthenticated || $[15] !== data?.orders || $[16] !== data?.subscriptions || $[17] !== error || $[18] !== isLoading || $[19] !== openAuthContinueModal) {
    t7 = !isLoading && !error && <div className="anim-enter mx-auto max-w-5xl p-4 [--enter-duration:500ms]">{currentUser.isAuthenticated ? <><div className="anim-enter mb-10 [--enter-duration:400ms] [--enter-y:20px]"><h2 className="mb-4 text-2xl font-semibold text-textPrimary">Orders</h2><div className="overflow-hidden rounded-xl border border-ds-border-standard shadow-sm"><table className="anim-enter w-full table-auto border-collapse"><thead className="bg-background-secondary"><tr className="text-textPrimary"><th className="px-4 py-3 text-left font-emphasis">Date Purchased</th><th className="px-4 py-3 text-left font-emphasis">Product</th><th className="px-4 py-3 text-left font-emphasis">Delivered</th><th className="px-4 py-3 text-left font-emphasis">Total</th><th className="px-4 py-3 text-left font-emphasis">Platform</th><th className="px-4 py-3 text-left font-emphasis">Status</th></tr></thead><tbody>{data?.orders.map(_temp3)}{data?.orders.length === 0 && <tr className="anim-enter [--enter-y:20px]"><td colSpan={6} className="border-t border-ds-border-subtle px-4 py-6 text-center text-textSecondary">No orders found.</td></tr>}</tbody></table></div></div><div className="anim-enter mb-8 [--enter-delay:200ms] [--enter-duration:400ms] [--enter-y:20px]"><h2 className="mb-4 text-2xl font-semibold text-textPrimary">Subscriptions</h2><div className="overflow-hidden rounded-xl border border-ds-border-standard shadow-sm"><table className="anim-enter w-full table-auto border-collapse"><thead className="bg-background-secondary"><tr className="text-textPrimary"><th className="px-4 py-3 text-left font-emphasis">#</th><th className="px-4 py-3 text-left font-emphasis">Status</th><th className="px-4 py-3 text-left font-emphasis">Type</th><th className="px-4 py-3 text-left font-emphasis">Price/month</th><th className="px-4 py-3 text-left font-emphasis">Next Payment Due</th><th className="px-4 py-3 text-left font-emphasis">Actions</th></tr></thead><tbody>{data?.subscriptions.map((subscription, index_0) => <tr key={subscription.subscriptionId} style={animDelay(index_0 * 50)} className="anim-enter [--enter-y:20px] text-textSecondary transition-colors hover:bg-background-light"><td className="border-t border-ds-border-subtle px-4 py-3">{index_0 + 1}</td><td className="border-t border-ds-border-subtle px-4 py-3"><span className={`inline-flex rounded-full px-2 py-1 text-xs font-emphasis ${subscription.nextPayment ? "bg-primary/10 text-primary" : "bg-muted text-muted-foreground"}`}>{subscription.nextPayment ? "Active" : "Inactive"}</span></td><td className="border-t border-ds-border-subtle px-4 py-3 font-emphasis">{subscription.type}</td><td className="border-t border-ds-border-subtle px-4 py-3">{formatUSD(subscription.price)}</td><td className="border-t border-ds-border-subtle px-4 py-3">{subscription.nextPayment ? new Date(subscription.nextPayment).toLocaleDateString() : "Cancelled"}</td><td className="border-t border-ds-border-subtle px-4 py-3">{subscription.nextPayment && <button className="rounded-md px-3 py-1 font-emphasis text-destructive transition-all hover:scale-[1.05] hover:bg-destructive/10 active:scale-[0.95]" onClick={() => cancelSubscriptionHandler(subscription.subscriptionId)}>Cancel</button>}{!subscription.nextPayment && <p>-</p>}</td></tr>)}{data?.subscriptions.length === 0 && <tr className="anim-enter [--enter-y:20px]"><td colSpan={6} className="border-t border-ds-border-subtle px-4 py-6 text-center text-textSecondary">No subscriptions found.</td></tr>}</tbody></table></div></div></> : <div className="anim-enter mb-10 [--enter-duration:400ms] [--enter-y:20px]"><h2 className="mb-4 text-2xl font-semibold text-textPrimary">Purchase History</h2><div className="overflow-hidden rounded-xl border border-ds-border-standard shadow-sm"><div className="px-4 py-6 text-center text-textSecondary">Please{" "}<button className="text-primary" onClick={openAuthContinueModal}>log in</button>{" "}to view your purchase history.</div></div></div>}</div>;
    $[13] = cancelSubscriptionHandler;
    $[14] = currentUser.isAuthenticated;
    $[15] = data?.orders;
    $[16] = data?.subscriptions;
    $[17] = error;
    $[18] = isLoading;
    $[19] = openAuthContinueModal;
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  const t8 = Boolean(error);
  let t9;
  if ($[21] !== isLoading || $[22] !== t8) {
    t9 = <Error loading={isLoading} error={t8} />;
    $[21] = isLoading;
    $[22] = t8;
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  let t10;
  if ($[24] !== cancelSubButtonLoading || $[25] !== cancelSubscriptionHandler || $[26] !== cancelSubscriptionModalOpen) {
    t10 = cancelSubscriptionModalOpen !== null && <Confirmation cancelHandler={() => setCancelSubscriptionModalOpen(null)} confirmHandler={() => cancelSubscriptionHandler(cancelSubscriptionModalOpen, true)} title="Cancel Subscription" message="You're about to cancel your subscription. Are you sure? This action cannot be undone." actionButtonText="Unsubscribe" actionButtonLoading={cancelSubButtonLoading} />;
    $[24] = cancelSubButtonLoading;
    $[25] = cancelSubscriptionHandler;
    $[26] = cancelSubscriptionModalOpen;
    $[27] = t10;
  } else {
    t10 = $[27];
  }
  let t11;
  if ($[28] !== t10 || $[29] !== t6 || $[30] !== t7 || $[31] !== t9) {
    t11 = <>{t6}{t7}{t9}{t10}</>;
    $[28] = t10;
    $[29] = t6;
    $[30] = t7;
    $[31] = t9;
    $[32] = t11;
  } else {
    t11 = $[32];
  }
  return t11;
};
export default PurchaseHistory;
function _temp() {
  return unwrapFromResult(client.products.listPurchases.query());
}
function _temp2(payment) {
  return payment.completed === null;
}
function _temp3(order, index) {
  const orderCreatedAt = new Date(order.createdAt);
  const currentTime = new Date();
  const failed = currentTime.getTime() - orderCreatedAt.getTime() > 300000 && order.payments.every(_temp2);
  const getStatus = () => {
    if (failed) {
      return "Failed";
    }
    if (order.payments[0]?.refunded !== null) {
      return `Refunded on ${new Date(order.payments[0]?.refunded).toLocaleString()}`;
    }
    if (order.payments[0]?.completed === null) {
      return "Pending";
    }
    return "Paid";
  };
  const getStatusClass = () => {
    if (failed) {
      return "text-destructive";
    }
    if (order.payments[0]?.completed === null) {
      return "text-accent-foreground";
    }
    return "text-primary";
  };
  return <tr key={order.id} style={animDelay(index * 50)} className="anim-enter [--enter-y:20px] text-textSecondary transition-colors hover:bg-background-light"><td className="border-t border-ds-border-subtle px-4 py-3"><div className="flex flex-col"><span className="font-emphasis text-textPrimary">{new Date(order.createdAt).toLocaleDateString("en-US", {
            weekday: "short",
            month: "short",
            day: "numeric",
            year: "numeric"
          })}</span><span className="text-xs text-textSecondary">{new Date(order.createdAt).toLocaleTimeString("en-US", {
            hour: "2-digit",
            minute: "2-digit"
          })}</span></div></td><td className="border-t border-ds-border-subtle px-4 py-3 font-emphasis">{order.items[0].product.title}</td><td className="border-t border-ds-border-subtle px-4 py-3 text-sm text-textSecondary">{order.payments[0]?.tronsDelivered != null && order.payments[0].tronsDelivered > 0 ? `${order.payments[0].tronsDelivered.toLocaleString()} trons` : order.items[0].product.type === "DONATION" ? "Donation" : order.payments[0]?.premiumDaysDelivered != null ? `${order.payments[0].premiumDaysDelivered} days premium` : "-"}</td><td className="border-t border-ds-border-subtle px-4 py-3">{formatUSD(order.total)}</td><td className="border-t border-ds-border-subtle px-4 py-3"><span className="inline-flex rounded-full bg-background-light px-2 py-0.5 text-xs text-textSecondary">{order.payments[0]?.type === "APPLE" ? "App Store" : order.payments[0]?.type === "GOOGLE" ? "Google Play" : "Stripe"}</span></td><td className={`border-t border-ds-border-subtle px-4 py-3 font-emphasis ${getStatusClass()}`}>{getStatus()}</td></tr>;
}